import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { analyseTeam, ApiError, fetchPlMatch, syncRoster } from "../api";
import type { AnalysisGame, AnalysisResult, AnalysisSide, PlMatch } from "../types";
import { getChampionIconUrl, getChampionName, getMapUrl } from "../utils/ddragon";
import { commonTeam, draftStats, earlyPlan, fromBlue, gamePlan, keyPoints, lineup, plMatchIds, toAnalysisGame, type ChampCount, type DraftStats } from "../utils/primeleague";
import { parseRoster } from "../utils/regions";
import { card, Champs, PlayerCard, ROLE } from "./ScoutPage";
import { ScoreboardPanel } from "./summoner/Scoreboard";
import { formatDuration } from "./summoner/parts";
import { button, label, Skeleton } from "./ui/primitives";

const MAX_ROSTER = 7; // same cap as the API
const LINKS_KEY = "riftwatch.analysis.links";
// ponytail: Prime League is EU West
const REGION = "euw";
const ROLE_ORDER = Object.keys(ROLE);
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const short = (riotId: string) => riotId.split("#")[0];
const dateText = (ms: number) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const saved = (key: string) => {
	try {
		return localStorage.getItem(key) ?? "";
	} catch {
		return "";
	}
};
const store = (key: string, value: string) => {
	try {
		localStorage.setItem(key, value);
	} catch {
		/* not persisted */
	}
};
const linkOf = (id: number | string) => `https://www.primeleague.gg/de/leagues/matches/${id}`;
const tagsOf = (matches: PlMatch[]) => [...new Set(matches.flatMap(m => Object.keys(m.teams)))];
/** Every game of these matches seen from `team`, one per Riot match id. */
const gamesOf = (matches: PlMatch[], team: string) => [...new Map(matches.flatMap(m => m.games.flatMap(g => toAnalysisGame(g, team) ?? [])).map(g => [g.matchId, g])).values()];

/**
 * The linked PL games, filled in from Riot's copy of the same game where there is one (comp styles, role positions).
 * Only linked games: Riot also knows the line-up's other tournament games, which the user did not ask about.
 */
function mergeGames(riot: AnalysisGame[], pl: AnalysisGame[]): AnalysisGame[] {
	const byId = new Map(riot.map(g => [g.matchId, g]));
	return pl
		.map(p => {
			const r = byId.get(p.matchId);
			// same game, maybe seen from the other team: pair sides by colour
			const fill = (s: AnalysisSide): AnalysisSide => (s.bans.length ? s : { ...s, bans: (s.side === p.us.side ? p.us : p.them).bans });
			return r ? { ...r, order: p.order, us: fill(r.us), them: fill(r.them) } : p;
		})
		.sort((a, b) => b.date - a.date);
}

const h2 = "m-0 text-[17px] font-extrabold [font-stretch:108%]";

/** Champions they keep coming back to as rows (count, then the team's record); one-offs only as a line of icons. */
function ChampCounts({ title, champs }: { title: string; champs: ChampCount[] }) {
	const repeats = champs.filter(c => c.count > 1).slice(0, 5);
	const rows = repeats.length ? repeats : champs.slice(0, 3);
	const once = champs.filter(c => !rows.includes(c));
	return (
		<div className="min-w-0">
			<div className={cn(label, "mb-2")}>{title}</div>
			{champs.length ? (
				<>
					<ul className="m-0 flex list-none flex-col gap-2 p-0">
						{rows.map(c => (
							<li key={c.championId} className="flex items-center gap-2 text-sm">
								<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-7 w-7 rounded-md" />
								<span className="min-w-0 flex-1 truncate font-semibold">{getChampionName(c.championId)}</span>
								<span className="text-xs font-bold text-ink">{c.count}×</span>
								<span className="w-12 text-right text-xs text-ink-3">
									{c.wins}W {c.count - c.wins}L
								</span>
							</li>
						))}
					</ul>
					{once.length > 0 && (
						<div className="mt-2.5 flex items-start gap-2 text-xs text-ink-3">
							<span className="shrink-0 leading-5">Once</span>
							<Champs ids={once.slice(0, 8).map(c => c.championId)} size={20} />
							{once.length > 8 && <span className="shrink-0 leading-5">+{once.length - 8}</span>}
						</div>
					)}
				</>
			) : (
				<div className="text-[13px] text-ink-3">None</div>
			)}
		</div>
	);
}

/** The answer first: what to ban, what they open with, who carries, which lane falls behind. The sections below are the evidence. */
function GamePlanCard({ d, games }: { d: DraftStats; games: AnalysisGame[] }) {
	const p = gamePlan(games);
	const none = (t: string) => <div className="text-[13px] text-ink-3">{t}</div>;
	const champ = (c: ChampCount) => (
		<span key={c.championId} className="flex items-center gap-1.5" title={`${c.count} games, ${c.wins}W ${c.count - c.wins}L`}>
			<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-6 w-6 rounded-[5px]" />
			<span className="text-[13px] font-semibold">{getChampionName(c.championId)}</span>
			<span className="text-xs text-ink-3">{c.count}×</span>
		</span>
	);
	return (
		<section className={cn(card, "border-signal")} aria-labelledby="plan-h">
			<h2 id="plan-h" className={h2}>
				Game plan
			</h2>
			<p className="m-0 mt-1 mb-4 text-xs text-ink-3">
				From {games.length} {games.length === 1 ? "game" : "games"}
				{games.length < 5 && ": a small sample, treat it as a hint"}.
			</p>
			<div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
				<div className="min-w-0">
					<div className={cn(label, "mb-2")}>Ban</div>
					{p.bans.length ? (
						<ul className="m-0 flex list-none flex-col gap-2 p-0">
							{p.bans.map(c => (
								<li key={c.championId} className="flex items-center gap-2 text-sm">
									<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-7 w-7 rounded-md" />
									<span className="min-w-0 flex-1">
										<span className="block truncate font-semibold">{getChampionName(c.championId)}</span>
										<span className="block truncate text-xs text-ink-3">
											{short(c.riotId)} · {c.count}× {c.wins}W {c.count - c.wins}L
										</span>
									</span>
								</li>
							))}
						</ul>
					) : (
						none("No champion played twice yet")
					)}
				</div>
				<div className="min-w-0">
					<div className={cn(label, "mb-2")}>They open with</div>
					{d.ordered ? (
						<div className="flex flex-col gap-2">
							<div className="text-xs font-bold text-side-blue">Blue first pick</div>
							{d.firstPickBlue.length ? <div className="flex flex-wrap gap-x-3 gap-y-1.5">{d.firstPickBlue.slice(0, 2).map(champ)}</div> : none("Not on blue yet")}
							<div className="text-xs font-bold text-side-red">Red first two</div>
							{d.firstPicksRed.length ? <div className="flex flex-wrap gap-x-3 gap-y-1.5">{d.firstPicksRed.slice(0, 3).map(champ)}</div> : none("Not on red yet")}
						</div>
					) : (
						none("No pick order: only group-phase match pages have it")
					)}
				</div>
				<div className="min-w-0">
					<div className={cn(label, "mb-2")}>Main carry</div>
					{p.carry ? (
						<>
							<div className="truncate text-[15px] font-bold">{short(p.carry.riotId)}</div>
							<div className="mt-0.5 text-xs text-ink-2">
								{ROLE[p.carry.role] ?? p.carry.role} · {Math.round(p.carry.killShare * 100)}% of the team&apos;s kills · {p.carry.kda.toFixed(1)} KDA
							</div>
						</>
					) : (
						none("Needs 2+ games")
					)}
				</div>
				<div className="min-w-0">
					<div className={cn(label, "mb-2")}>Weak lane</div>
					{p.weak ? (
						<>
							<div className="truncate text-[15px] font-bold">
								{ROLE[p.weak.role] ?? p.weak.role} · {short(p.weak.riotId)}
							</div>
							<div className="mt-0.5 text-xs text-ink-2">
								{Math.round(p.weak.goldDiff)} gold and {p.weak.csDiff > 0 ? "+" : ""}
								{Math.round(p.weak.csDiff)} CS at 15 against their lane opponent ({p.weak.games} {p.weak.games === 1 ? "game" : "games"})
							</div>
						</>
					) : (
						none(games.length < 2 ? "Needs 2+ games" : games.some(g => g.us.players.some(x => x.at15)) ? "No lane behind at 15" : "Needs the Riot timelines")
					)}
				</div>
			</div>
			<EarlyRow games={games} />
			<MapRow games={games} />
		</section>
	);
}

const OBJECTIVE: Record<string, string> = { DRAGON: "Dragon", HORDE: "Grubs", RIFTHERALD: "Herald", BARON_NASHOR: "Baron", ATAKHAN: "Atakhan" };
const time = (s: number) => formatDuration(Math.round(s));
const signed = (n: number) => `${n > 0 ? "+" : ""}${Math.round(n)}`;

/** The Game plan's second row, from the Riot timelines: jungle habits, early lead, who dies first, objectives. */
function EarlyRow({ games }: { games: AnalysisGame[] }) {
	const e = earlyPlan(games);
	const none = (t: string) => <div className="text-[13px] text-ink-3">{t}</div>;
	const line = "mt-0.5 text-xs text-ink-2";
	if (!e.games) return <p className="m-0 mt-5 border-t border-line pt-4 text-[13px] text-ink-3">The early game (jungle, gold at 15, first deaths, objectives) needs the Riot timelines.</p>;
	const { jungle: j, tempo: t, firstDeath: d, objectives: o } = e;
	const side = j.top + j.bot ? (j.top >= j.bot ? { half: "top", n: j.top } : { half: "bot", n: j.bot }) : null;
	return (
		<div className="mt-5 grid gap-x-8 gap-y-5 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-4">
			<div className="min-w-0">
				<div className={cn(label, "mb-2")}>Jungle</div>
				{side ? (
					<div className="text-[15px] font-bold">
						Starts {side.half} side · {side.n} of {j.top + j.bot}
					</div>
				) : (
					none("No start position")
				)}
				{j.ganks.length > 0 && (
					<div className={line}>
						First gank {j.ganks.slice(0, 3).map(g => `${ROLE[g.lane] ?? g.lane} ${g.count}×`).join(", ")} · around {time(j.gankT ?? 0)}
					</div>
				)}
				{j.ganks.length === 0 && <div className={line}>No early gank kills</div>}
				{e.presence && e.presence.minutes > 0 && (
					<div className={line}>
						Minutes 2-14: {pct(e.presence.top, e.presence.minutes)}% top side, {pct(e.presence.invading, e.presence.minutes)}% in the enemy jungle
					</div>
				)}
				{e.lvl1.won + e.lvl1.lost > 0 && (
					<div className={line}>
						Level 1 fights in {e.lvl1.won + e.lvl1.lost} of {e.games}, won {e.lvl1.won}
					</div>
				)}
			</div>
			<div className="min-w-0">
				<div className={cn(label, "mb-2")}>Gold at 15</div>
				{t ? (
					<>
						<div className="text-[15px] font-bold">{t.avg >= 0 ? `Ahead early · ${signed(t.avg)} avg` : `Behind early · ${signed(t.avg)} avg`}</div>
						<div className={line}>
							Ahead in {t.ahead} of {t.games}, won {t.aheadWins} of those
							{t.ahead >= 2 && t.aheadWins * 2 < t.ahead && ": they throw leads"}
						</div>
						<div className={line}>
							Behind in {t.behind}, won {t.behindWins}
							{t.behind >= 2 && t.behindWins * 2 >= t.behind && ": they come back"}
						</div>
					</>
				) : (
					none("No game reached 15:00")
				)}
			</div>
			<div className="min-w-0">
				<div className={cn(label, "mb-2")}>Dies first</div>
				{d ? (
					<>
						<div className="truncate text-[15px] font-bold">
							{short(d.riotId)} · {ROLE[d.role] ?? d.role}
						</div>
						<div className={line}>
							First death in {d.count} of {e.games}, {d.ganked} to their jungler
						</div>
						<div className={line}>{d.deaths10.toFixed(1)} deaths before 10:00 a game</div>
					</>
				) : (
					none(e.games < 2 ? "Needs 2+ games" : "Nobody dies first twice")
				)}
			</div>
			<div className="min-w-0">
				<div className={cn(label, "mb-2")}>Objectives</div>
				{o.first.length ? (
					<div className="text-[15px] font-bold">
						First {o.first.slice(0, 2).map(x => `${OBJECTIVE[x.type] ?? x.type} ${x.count}×`).join(", ")}
					</div>
				) : (
					none("No epic monster taken")
				)}
				{o.dragonGames > 0 && (
					<div className={line}>
						First dragon in {o.dragons} of {o.dragonGames}
						{o.dragonT !== null && ` · around ${time(o.dragonT)}`}
					</div>
				)}
			</div>
		</div>
	);
}

const MAP = 14800;
const ROLE_COLOR: Record<string, string> = { TOP: "#f59e0b", JUNGLE: "#c6f432", MIDDLE: "#7fb2ff", BOTTOM: "#f472b6", UTILITY: "#a78bfa" };
/** map coordinates (y up) to SVG (y down), red-side games mirrored to blue */
const onMap = (side: "blue" | "red", p: [number, number]) => {
	const [x, y] = fromBlue(side, p);
	return [x, MAP - y] as const;
};

function Minimap({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
	return (
		<figure className="m-0 min-w-0">
			<figcaption className={cn(label, "mb-2")}>{title}</figcaption>
			<svg viewBox={`0 0 ${MAP} ${MAP}`} role="img" aria-label={`${title}: ${desc}`} className="block aspect-square w-full max-w-[280px] rounded-lg border border-line bg-sunken">
				<image href={getMapUrl()} width={MAP} height={MAP} opacity={0.5} />
				{children}
			</svg>
			<p className="m-0 mt-1.5 max-w-[280px] text-xs text-ink-3">{desc}</p>
		</figure>
	);
}

/** The Game plan's third row: the jungler's first 8 minutes and the early deaths on the minimap, then lane swaps and roams. */
function MapRow({ games }: { games: AnalysisGame[] }) {
	const tl = games.filter(g => g.early);
	if (!tl.length) return null;
	const e = earlyPlan(games);
	const line = "mt-0.5 text-xs text-ink-2";
	return (
		<div className="mt-5 grid gap-x-8 gap-y-5 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-3">
			<Minimap title="Jungle path · 1-8 min" desc="A dot a minute, the big one at 8:00. Red-side games are mirrored, so their base is always bottom left.">
				{tl.map(g => {
					const pts = (g.early!.junglePath ?? []).filter(([x, y]) => x || y).map(p => onMap(g.us.side, p));
					const color = `var(--color-side-${g.us.side})`;
					return (
						<g key={g.matchId} fill={color} stroke={color}>
							<polyline points={pts.map(p => p.join(",")).join(" ")} fill="none" strokeWidth={80} strokeOpacity={0.6} strokeLinejoin="round" />
							{pts.map((p, i) => (
								<circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 260 : 130} stroke="none" />
							))}
						</g>
					);
				})}
			</Minimap>
			<Minimap title="Deaths · before 15 min" desc="Where they died, by role. Red-side games are mirrored like the jungle path.">
				{tl.flatMap(g =>
					g.early!.deaths.map((d, i) => {
						const [x, y] = onMap(g.us.side, [d.x, d.y]);
						return <circle key={`${g.matchId}-${i}`} cx={x} cy={y} r={240} fill={ROLE_COLOR[d.role] ?? "#eef1f5"} stroke="#0c0f14" strokeWidth={60} />;
					})
				)}
			</Minimap>
			<div className="min-w-0">
				<div className={cn(label, "mb-2")}>Movement</div>
				<div className="text-[15px] font-bold">{e.laneSwaps ? `Lane swap in ${e.laneSwaps} of ${e.games}` : "No lane swaps"}</div>
				<div className={line}>{e.laneSwaps ? "Their bot lane went top in the first minutes" : "Their bot lane stays bot early"}</div>
				{e.roams.map(r => (
					<div key={r.role} className="mt-3">
						<div className="text-sm font-bold">
							{ROLE[r.role] ?? r.role} roams in {r.games} of {e.games}
						</div>
						<div className={line}>
							{r.minutes.toFixed(1)} of minutes 3-14 away from lane a game{r.first !== null && ` · first around minute ${Math.round(r.first)}`}
						</div>
					</div>
				))}
				<ul className="m-0 mt-4 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-xs text-ink-2" aria-label="Death map colors">
					{Object.entries(ROLE_COLOR).map(([role, color]) => (
						<li key={role} className="flex items-center gap-1.5">
							<span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
							{ROLE[role]}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}

/** What the roster picked on each role over all games: champion, games, wins. */
function picksByRole(games: AnalysisGame[]) {
	const out = new Map<string, Map<number, { games: number; wins: number }>>();
	for (const g of games)
		for (const p of g.us.players) {
			const role = out.get(p.role) ?? new Map();
			const r = role.get(p.championId) ?? { games: 0, wins: 0 };
			r.games++;
			if (g.us.win) r.wins++;
			role.set(p.championId, r);
			out.set(p.role, role);
		}
	return ROLE_ORDER.filter(r => out.has(r)).map(role => ({ role, champs: [...out.get(role)!].map(([championId, r]) => ({ championId, ...r })).sort((a, b) => b.games - a.games || b.wins - a.wins) }));
}

/** Bans in draft order, split into the two ban phases (turns 1-6, 7-10). */
function Bans({ bans }: { bans: AnalysisSide["bans"] }) {
	const phase1 = bans.filter(b => b.turn <= 6).map(b => b.championId);
	const phase2 = bans.filter(b => b.turn > 6).map(b => b.championId);
	return (
		<span className="flex items-center gap-2 text-xs text-ink-3">
			Bans
			<Champs ids={phase1} size={20} />
			{phase2.length > 0 && <span aria-hidden="true" className="h-4 w-px bg-line-2" />}
			<Champs ids={phase2} size={20} />
		</span>
	);
}

/** The whole draft as it happened, like the PL page: ban 1, pick 1, ban 2, pick 2 (6 / 6 / 4 / 4 actions). */
function DraftStrip({ order }: { order: NonNullable<AnalysisGame["order"]> }) {
	const groups = [order.slice(0, 6), order.slice(6, 12), order.slice(12, 16), order.slice(16)];
	return (
		<ol aria-label="Draft order" className="m-0 mb-3 flex list-none flex-wrap items-center gap-x-3 gap-y-1.5 p-0">
			{groups.map((grp, i) => (
				<li key={i} className="flex gap-1">
					{grp.map((d, j) => (
						<img
							key={j}
							src={getChampionIconUrl(d.championId)}
							alt={`${d.side === "blue" ? "Blue" : "Red"} ${d.kind}: ${getChampionName(d.championId)}`}
							title={`${d.side === "blue" ? "Blue" : "Red"} ${d.kind}: ${getChampionName(d.championId)}`}
							loading="lazy"
							className={cn("h-7 w-7 rounded-[5px] border-2", d.side === "blue" ? "border-side-blue" : "border-side-red", d.kind === "ban" && "opacity-60 grayscale")}
						/>
					))}
				</li>
			))}
		</ol>
	);
}

function SideBlock({ s, duration, us }: { s: AnalysisSide; duration: number; us: boolean }) {
	const min = duration / 60;
	return (
		<div className="min-w-0 flex-1">
			<div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
				<span className={cn("text-xs font-bold uppercase tracking-[.08em]", s.side === "blue" ? "text-side-blue" : "text-side-red")}>
					{us ? "Team" : "Opponent"} · {s.side === "blue" ? "Blue" : "Red"}
				</span>
				{s.styles.length > 0 && <span className="text-xs text-ink-2">{s.styles.join(", ")}</span>}
				{s.bans.length > 0 && <Bans bans={s.bans} />}
			</div>
			<ul className="m-0 flex list-none flex-col gap-1 p-0">
				{s.players.map(p => (
					<li key={p.riotId + p.championId} className="grid grid-cols-[40px_24px_minmax(0,1fr)_auto] items-center gap-2 text-[13px]">
						<span className="text-xs text-ink-3">{ROLE[p.role] ?? "—"}</span>
						<img src={getChampionIconUrl(p.championId)} alt="" loading="lazy" className="h-6 w-6 rounded-[5px]" />
						<span className="min-w-0 truncate">
							<span className="font-semibold">{getChampionName(p.championId) === "Unknown" ? p.championName : getChampionName(p.championId)}</span>{" "}
							<span className="text-xs text-ink-3">{short(p.riotId)}</span>
						</span>
						<span className="whitespace-nowrap font-mono text-xs text-ink-2">
							{p.kills}/{p.deaths}/{p.assists} · {min ? (p.cs / min).toFixed(1) : 0} cs/m · {pct(p.kills + p.assists, s.kills)}% kp
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}

/** One line per game (result, side, both comps); the draft, line-ups and scoreboard open on click. */
function GameRow({ g, region }: { g: AnalysisGame; region: string }) {
	const [open, setOpen] = useState(false);
	const [board, setBoard] = useState(false);
	const id = `game-${g.matchId}`;
	const comp = (s: AnalysisSide) => s.players.map(p => p.championId);
	return (
		<li className={cn("overflow-hidden rounded-[10px] border bg-surface", g.us.win ? "border-win-line" : "border-line")}>
			<button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={id} className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-2.5 text-left hover:bg-raised">
				<span className={cn("w-9 text-sm font-extrabold", g.us.win ? "text-signal" : "text-ink-2")}>{g.us.win ? "Win" : "Loss"}</span>
				<span className="w-[104px] text-xs text-ink-3">
					{dateText(g.date)} · <span className="font-mono">{formatDuration(g.duration)}</span>
				</span>
				<span className={cn("w-8 text-xs font-bold", g.us.side === "blue" ? "text-side-blue" : "text-side-red")}>{g.us.side === "blue" ? "Blue" : "Red"}</span>
				<span className="flex items-center gap-2">
					<Champs ids={comp(g.us)} size={24} />
					<span className="text-xs text-ink-3">vs</span>
					<span className="opacity-70">
						<Champs ids={comp(g.them)} size={24} />
					</span>
				</span>
				<ChevronDown aria-hidden="true" className={cn("ml-auto h-4 w-4 text-ink-3 transition-transform", open && "rotate-180")} />
			</button>
			{open && (
				<div id={id} className="border-t border-line px-3.5 py-3">
					<p className="m-0 mb-2 text-xs text-ink-3">
						{g.order ? "Draft in order: bans grey, blue and red borders show the side" : g.draft ? "In-client tournament draft: bans only, pick order unknown" : "Drafted outside the client: no draft data"}
					</p>
					{g.order && <DraftStrip order={g.order} />}
					<div className="flex flex-col gap-4 md:flex-row">
						<SideBlock s={g.us} duration={g.duration} us />
						<SideBlock s={g.them} duration={g.duration} us={false} />
					</div>
					<button type="button" onClick={() => setBoard(b => !b)} aria-expanded={board} className={cn(button.secondary, "mt-3 h-8 px-3 text-xs")}>
						Scoreboard
						<ChevronDown aria-hidden="true" className={cn("h-3 w-3 transition-transform", board && "rotate-180")} />
					</button>
					{board && (
						<div className="mt-3 border-t border-line pt-3">
							<ScoreboardPanel matchId={g.matchId} region={region} me="" />
						</div>
					)}
				</div>
			)}
		</li>
	);
}

function Result({ games, matches, riot, region }: { games: AnalysisGame[]; matches: number; riot: AnalysisResult | null; region: string }) {
	const t = riot?.team;
	const rec = (gs: AnalysisGame[]) => {
		const w = gs.filter(g => g.us.win).length;
		return `${w}W ${gs.length - w}L`;
	};
	const tiles = [
		{ v: rec(games), l: `${matches} ${matches === 1 ? "match" : "matches"} · ${games.length} games` },
		{ v: rec(games.filter(g => g.us.side === "blue")), l: "Blue side" },
		{ v: rec(games.filter(g => g.us.side === "red")), l: "Red side" },
		{ v: formatDuration(Math.round(games.reduce((a, g) => a + g.duration, 0) / (games.length || 1))), l: "Avg game" },
		...(t
			? [
					{ v: `${t.first.blood}%`, l: "First blood" },
					{ v: `${t.first.tower}%`, l: "First tower" },
					{ v: `${t.first.dragon}%`, l: "First dragon" },
					{ v: `${t.first.grubs}%`, l: "First grubs" }
				]
			: [])
	];
	const d = draftStats(games);
	const points = keyPoints(d, games);
	if (!games.length) return <p className="mt-6 max-w-[640px] text-sm text-ink-2">None of these matches has a played game yet.</p>;
	return (
		<div className="mt-8 flex flex-col gap-4">
			<GamePlanCard d={d} games={games} />

			<section className={card} aria-labelledby="sum-h">
				<h2 id="sum-h" className={cn(h2, "mb-3")}>
					Overview <span className="text-sm font-semibold text-ink-3 [font-stretch:100%]">since {dateText(Math.min(...games.map(g => g.date)))}</span>
				</h2>
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					{tiles.map(x => (
						<div key={x.l} className="rounded-lg border border-line bg-raised p-2.5">
							<div className="text-lg font-extrabold [font-stretch:112%]">{x.v}</div>
							<div className="mt-px text-xs text-ink-2">{x.l}</div>
						</div>
					))}
				</div>
				{points.length > 0 && (
					<ul className="m-0 mt-4 flex list-none flex-col gap-2 p-0">
						{points.map(p => (
							<li key={p} className="flex gap-2.5 text-[15px] leading-snug text-ink">
								<span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-signal" />
								{p}
							</li>
						))}
					</ul>
				)}
			</section>

			<section className={card} aria-labelledby="picks-h">
				<h2 id="picks-h" className={h2}>
					Picks
				</h2>
				{d.ordered ? (
					<>
						<p className="m-0 mt-1 mb-4 text-xs text-ink-3">
							From the {d.ordered} {d.ordered === 1 ? "game" : "games"} with the full draft. Count, then their record with it.
						</p>
						<div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
							<ChampCounts title="First pick on blue" champs={d.firstPickBlue} />
							<ChampCounts title="First two on red" champs={d.firstPicksRed} />
							<ChampCounts title="Early picks · 1-3" champs={d.picks1} />
							<ChampCounts title="Late picks · 4-5" champs={d.picks2} />
						</div>
						<div className="mt-5 border-t border-line pt-4">
							<div className={cn(label, "mb-2")}>Usual pick order</div>
							<ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-2 p-0 text-sm">
								{d.roleOrder.map((r, i) => (
									<li key={r.role} className="flex items-center gap-2">
										{i > 0 && (
											<span aria-hidden="true" className="text-ink-3">
												→
											</span>
										)}
										<span className="rounded-md border border-line bg-raised px-2 py-1 font-bold" title={`average pick ${r.avg.toFixed(1)} of 5`}>
											{ROLE[r.role] ?? r.role}
										</span>
									</li>
								))}
							</ol>
						</div>
					</>
				) : (
					<p className="m-0 mt-1 text-[13px] text-ink-3">No pick order yet: only group-phase match pages have the full draft.</p>
				)}
			</section>

			<section className={card} aria-labelledby="bans-h">
				<h2 id="bans-h" className={cn(h2, "mb-4")}>
					Bans
				</h2>
				<div className="grid gap-4 lg:grid-cols-2">
					{[
						{ title: "They ban", p1: d.bans1, p2: d.bans2 },
						{ title: "Banned against them", p1: d.against1, p2: d.against2 }
					].map(x => (
						<div key={x.title} className="rounded-lg border border-line bg-sunken p-3">
							<div className="mb-3 text-sm font-bold">{x.title}</div>
							<div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
								<ChampCounts title="Phase 1" champs={x.p1} />
								<ChampCounts title="Phase 2" champs={x.p2} />
							</div>
						</div>
					))}
				</div>
				{d.targeted.length > 0 && (
					<div className="mt-5 border-t border-line pt-4">
						<div className={cn(label, "mb-1")}>Banned out of their pool</div>
						<p className="m-0 mb-2 text-xs text-ink-3">Bans against them on champions this player played in these games.</p>
						<ul className="m-0 list-none p-0">
							{d.targeted.map(p => (
								<li key={p.riotId} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-1.5 text-xs">
									<span className="w-32 truncate text-[13px] font-bold">{short(p.riotId)}</span>
									{p.champs.slice(0, 4).map(c => (
										<span key={c.championId} className="flex items-center gap-1.5">
											<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-6 w-6 rounded-[5px]" />
											<span className="font-semibold">{getChampionName(c.championId)}</span>
											<span className="text-ink-3">{c.count}×</span>
										</span>
									))}
								</li>
							))}
						</ul>
					</div>
				)}
			</section>

			<section className={card} aria-labelledby="pools-h">
				<h2 id="pools-h" className={cn(h2, "mb-2")}>
					Champion pools
				</h2>
				<ul className="m-0 list-none p-0">
					{picksByRole(games).map(r => (
						<li key={r.role} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line py-2.5 first:border-t-0">
							<span className="w-16 text-sm font-bold">{ROLE[r.role]}</span>
							{r.champs.map(c => (
								<span key={c.championId} className="flex items-center gap-1.5 text-xs">
									<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-6 w-6 rounded-[5px]" />
									<span className="font-semibold">{getChampionName(c.championId)}</span>
									<span className="text-ink-3">
										{c.wins}W {c.games - c.wins}L
									</span>
								</span>
							))}
						</li>
					))}
				</ul>
			</section>

			{riot && (
				<section aria-label="Players" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{riot.players.map(p => (
						<PlayerCard key={p.riotId} p={p} region={riot.region} />
					))}
				</section>
			)}

			<section aria-labelledby="games-h">
				<h2 id="games-h" className={cn(h2, "mt-2 mb-3")}>
					Games
				</h2>
				<ul className="m-0 flex list-none flex-col gap-2 p-0">
					{games.map(g => (
						<GameRow key={g.matchId} g={g} region={region} />
					))}
				</ul>
			</section>
		</div>
	);
}

export function AnalysisPage() {
	const [params, setParams] = useSearchParams();
	const [text, setText] = useState(() => (params.getAll("m").length ? params.getAll("m").map(linkOf).join("\n") : saved(LINKS_KEY)));
	const [pl, setPl] = useState<PlMatch[]>([]);
	const [picked, setPicked] = useState(() => params.get("team"));
	const [busy, setBusy] = useState(false);
	const [progress, setProgress] = useState("");
	const [errors, setErrors] = useState<string[]>([]);
	const [riot, setRiot] = useState<AnalysisResult | null>(null);
	const [riotBusy, setRiotBusy] = useState(false);
	const [riotError, setRiotError] = useState<string | null>(null);
	const ids = plMatchIds(text);

	const tags = tagsOf(pl);
	const team = picked && tags.includes(picked) ? picked : commonTeam(pl);
	const plGames = team ? gamesOf(pl, team) : [];
	const games = mergeGames(riot?.games ?? [], plGames);

	// The extra Riot side (objectives, player cards): the team's line-up from the PL pages, from its first game on.
	const loadRiot = async (gs: AnalysisGame[]) => {
		const roster = parseRoster(lineup(gs, MAX_ROSTER).join("\n"));
		setRiot(null);
		setRiotError(null);
		if (!roster.length) return;
		setRiotBusy(true);
		try {
			const riotIds = await syncRoster(REGION, roster, setProgress);
			setProgress("Reading the Riot games");
			setRiot(await analyseTeam(REGION, riotIds, Math.min(...gs.map(g => g.date)) - 86400_000, gs.map(g => g.matchId)));
		} catch (err) {
			const e = err instanceof ApiError ? err : null;
			setRiotError(e?.status === 429 ? `Riot is busy, try again in ${e.retryAfterSeconds ?? 30}s` : (e?.message ?? "Riot did not answer"));
		} finally {
			setRiotBusy(false);
		}
	};

	const run = async () => {
		if (!ids.length || busy) return;
		setBusy(true);
		setErrors([]);
		store(LINKS_KEY, text);
		// the address bar is the shareable link: opening it reads the same matches (from D1 by then)
		setParams({ m: ids.map(String), ...(picked ? { team: picked } : {}) }, { replace: true });
		const got: PlMatch[] = [];
		const failed: string[] = [];
		// one match per request: the worker reads at most one PL page each
		for (const [i, id] of ids.entries()) {
			setProgress(`Reading match ${i + 1} of ${ids.length} from primeleague.gg`);
			try {
				got.push(await fetchPlMatch(id));
			} catch (err) {
				failed.push(`${linkOf(id)}: ${err instanceof ApiError ? err.message : "could not be read"}`);
			}
		}
		setPl(got);
		setErrors(failed);
		setBusy(false);
		const t = picked && tagsOf(got).includes(picked) ? picked : commonTeam(got);
		if (t) void loadRiot(gamesOf(got, t));
	};

	const pickTeam = (t: string) => {
		setPicked(t);
		setParams({ m: ids.map(String), team: t }, { replace: true });
		void loadRiot(gamesOf(pl, t));
	};

	// a shared link: run it once
	useEffect(() => {
		if (params.getAll("m").length) void run();
	}, []);

	return (
		<section className="pt-8 sm:pt-12">
			<h1 className="m-0 text-[30px] font-extrabold leading-[1.05] [font-stretch:118%] sm:text-[40px]">Review a split.</h1>
			<p className="mt-3 mb-5 max-w-[640px] text-[15px] leading-normal text-ink-2 text-pretty">
				Paste a team&apos;s Prime League match links, yours or an opponent&apos;s. Every game shows the full draft in order, both sides&apos; bans and every
				player&apos;s line, plus the totals over the split and the team&apos;s Riot stats.
			</p>

			<form
				onSubmit={e => {
					e.preventDefault();
					void run();
				}}
				className="max-w-[560px]"
			>
				<label className="block">
					<span className="mb-1.5 flex items-baseline justify-between gap-2">
						<span className={label}>Prime League matches</span>
						<span className="text-xs text-ink-3" aria-live="polite">
							{ids.length} {ids.length === 1 ? "match" : "matches"} found
						</span>
					</span>
					<textarea
						value={text}
						onChange={e => setText(e.target.value)}
						rows={5}
						spellCheck={false}
						autoCapitalize="off"
						placeholder={"https://www.primeleague.gg/de/leagues/matches/1167909-…\nhttps://www.primeleague.gg/de/leagues/matches/…"}
						className="block w-full resize-y rounded-xl border border-line-2 bg-surface p-3.5 font-mono text-sm text-ink placeholder:text-ink-3 focus:border-signal focus:outline-none"
					/>
				</label>
				<div className="mt-3 flex flex-wrap items-center gap-3">
					<button type="submit" disabled={!ids.length || busy} className={button.primary}>
						{busy ? "Reading…" : "Review"}
					</button>
					{tags.length > 1 && team && !busy && (
						<label className="flex items-center gap-2 text-[13px] text-ink-2">
							Team
							<select value={team} onChange={e => pickTeam(e.target.value)} className="h-10 cursor-pointer rounded-lg border border-line-2 bg-raised px-2.5 text-[13px] font-bold text-ink">
								{tags.map(t => (
									<option key={t} value={t}>
										{pl.find(m => m.teams[t])?.teams[t] ?? t} ({t})
									</option>
								))}
							</select>
						</label>
					)}
				</div>
			</form>

			{errors.length > 0 && (
				<div role="alert" className="mt-5 max-w-[640px] rounded-[10px] border border-line-2 bg-raised px-3.5 py-3 text-sm text-ink">
					{errors.map(e => (
						<p key={e} className="m-0 break-words">
							{e}
						</p>
					))}
				</div>
			)}
			{(busy || riotBusy) && (
				<p role="status" className="m-0 mt-5 text-[13px] text-ink-2">
					{progress}…{riotBusy && " A team we have not seen takes up to a minute."}
				</p>
			)}
			{riotError && !riotBusy && <p className="m-0 mt-5 text-[13px] text-ink-3">Riot stats are missing ({riotError}). The drafts below are complete.</p>}
			{busy ? (
				<div aria-busy="true" className="mt-6 flex flex-col gap-3">
					<Skeleton className="h-[220px] rounded-[10px]" />
					<Skeleton className="h-[160px] rounded-[10px]" />
				</div>
			) : (
				plGames.length > 0 && <Result games={games} matches={pl.length} riot={riot} region={riot?.region ?? REGION} />
			)}
		</section>
	);
}
